import { useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, unwrap } from "../api.ts";
import { useMe } from "../hooks.ts";
import { Button, Card, cx, ErrorText, PageHeader, Spinner } from "../components/ui.tsx";
import { FilePreview } from "../components/FilePreview.tsx";
import { WorkspacePicker } from "../components/WorkspacePicker.tsx";
import { UsageBar } from "../components/runtime.tsx";
import { fileUrl, filesFromDrop, fmtSize, UploadError, uploadWithProgress, type DroppedFile } from "../lib/files.ts";
import { Icon, type IconName } from "../components/icons.tsx";

type Entry = { name: string; path: string; type: "file" | "dir" | "link"; size: number; modified: number };
type Sort = { key: "name" | "size" | "modified"; dir: 1 | -1 };
type Upload = { id: string; name: string; progress: number; error?: string };
/** One run of uploads (more files dropped while it runs join it). */
type Batch = { total: number; done: number; failed: number };
/** Failed rows shown; the rest are counted. */
const FAILED_SHOWN = 20;
const n = (x: number) => x.toLocaleString();

/** Marks a drag that started on a row here (moving files), as opposed to files dragged in from the computer. */
const MOVE_MIME = "application/x-krut-paths";
const join = (a: string, b: string) => (a && b ? `${a}/${b}` : a || b);
const icon = (f: Entry): IconName => (f.type === "dir" ? "files" : f.type === "link" ? "link" : /\.(png|jpe?g|gif|webp|svg)$/i.test(f.name) ? "image" : /\.(csv|xlsx?)$/i.test(f.name) ? "sheet" : /\.(pdf|docx?|md|txt)$/i.test(f.name) ? "fileText" : "file");

export function FilesPage() {
  const qc = useQueryClient();
  const me = useMe();
  const canUpload = me.data?.group?.permissions.fileUpload ?? false;
  const [path, setPath] = useState("");
  const [preview, setPreview] = useState<string | null>(null);
  // Only files uploading right now and ones that failed: a big folder is tens of thousands of files.
  const [uploads, setUploads] = useState<Upload[]>([]);
  const [batch, setBatch] = useState<Batch | null>(null);
  const failedFiles = useRef(new Map<string, { item: DroppedFile; into: string }>());
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState("");
  const [sort, setSort] = useState<Sort>({ key: "name", dir: 1 });
  const [moving, setMoving] = useState<string[] | null>(null);
  const [newFolder, setNewFolder] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const folderInput = useRef<HTMLInputElement>(null);

  const list = useQuery({ queryKey: ["files", path], queryFn: () => unwrap(api.me.assistant.files.$get({ query: { path } })) });
  const rt = useQuery({ queryKey: ["my-runtime"], queryFn: () => unwrap(api.me.runtime.$get()) });
  const refresh = () => void qc.invalidateQueries({ queryKey: ["files"] });
  const act = useMutation({
    mutationFn: async (fn: () => Promise<unknown>) => fn(),
    onSuccess: () => {
      setSelected(new Set());
      refresh();
    },
    onError: setError,
  });

  const go = (p: string) => {
    setPath(p);
    setSelected(new Set());
    setFilter("");
  };

  const items = useMemo(() => {
    const all = (list.data?.items ?? []) as Entry[];
    const shown = filter ? all.filter((f) => f.name.toLowerCase().includes(filter.toLowerCase())) : all;
    return [...shown].sort((a, b) => {
      if ((a.type === "dir") !== (b.type === "dir")) return a.type === "dir" ? -1 : 1; // folders first
      const v = sort.key === "name" ? a.name.localeCompare(b.name, undefined, { numeric: true }) : sort.key === "size" ? a.size - b.size : a.modified - b.modified;
      return v * sort.dir;
    });
  }, [list.data, filter, sort]);

  /* uploads: three at a time; brief failures (busy, network) are retried before a file is reported failed */
  const upload = async (files: DroppedFile[], into: string) => {
    if (!canUpload) return setError(new Error("File uploads aren't enabled for your group."));
    if (!files.length) return;
    setError(null);
    const queue = files.map((f) => ({ item: f, into, id: crypto.randomUUID() }));
    setBatch((b) => (b && b.done + b.failed < b.total ? { ...b, total: b.total + queue.length } : { total: queue.length, done: 0, failed: 0 }));
    let sinceRefresh = 0;
    const worker = async () => {
      for (let q = queue.shift(); q; q = queue.shift()) {
        const { item, id } = q;
        const name = join(item.dir, item.file.name);
        setUploads((u) => [...u, { id, name, progress: 0 }]);
        const onProgress = (p: number) => setUploads((u) => u.map((x) => (x.id === id ? { ...x, progress: p } : x)));
        try {
          for (let attempt = 1; ; attempt++) {
            try {
              await uploadWithProgress(item.file, join(q.into, item.dir), onProgress);
              break;
            } catch (e) {
              if (!(e instanceof UploadError) || !e.retry || attempt >= 3) throw e;
              await new Promise((r) => setTimeout(r, attempt * 3000));
            }
          }
          setUploads((u) => u.filter((x) => x.id !== id));
          setBatch((b) => b && { ...b, done: b.done + 1 });
          if (++sinceRefresh >= 200) {
            sinceRefresh = 0;
            refresh();
          }
        } catch (e) {
          if (e instanceof UploadError && e.retry) failedFiles.current.set(id, { item, into: q.into });
          setUploads((u) => u.map((x) => (x.id === id ? { ...x, error: (e as Error).message } : x)));
          setBatch((b) => b && { ...b, failed: b.failed + 1 });
        }
      }
    };
    await Promise.all([worker(), worker(), worker()]);
    refresh();
  };
  const retryFailed = () => {
    const again = [...failedFiles.current.entries()];
    failedFiles.current.clear();
    setUploads((u) => u.filter((x) => !x.error));
    setBatch(null);
    const byFolder = new Map<string, DroppedFile[]>();
    for (const [, f] of again) byFolder.set(f.into, [...(byFolder.get(f.into) ?? []), f.item]);
    for (const [into, files] of byFolder) void upload(files, into);
  };
  const dismissUploads = () => {
    failedFiles.current.clear();
    setUploads([]);
    setBatch(null);
  };
  const fromInput = (fl: FileList | null) =>
    Array.from(fl ?? []).map((file) => ({ file, dir: (file.webkitRelativePath || "").split("/").slice(0, -1).join("/") }));

  const move = (paths: string[], to: string) => {
    const real = paths.filter((p) => p !== to && !to.startsWith(p + "/") && p.split("/").slice(0, -1).join("/") !== to);
    if (real.length) act.mutate(() => unwrap(api.me.assistant.files.move.$post({ json: { to, paths: real } })));
  };

  /* drag & drop: rows onto folders (move), computer files onto the list or a folder (upload) */
  const dropProps = (target: string) => ({
    onDragOver: (e: React.DragEvent) => {
      const internal = e.dataTransfer.types.includes(MOVE_MIME);
      if (!internal && !canUpload) return;
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = internal ? "move" : "copy";
      setDropTarget(target);
    },
    onDragLeave: (e: React.DragEvent) => {
      e.stopPropagation();
      setDropTarget((t) => (t === target ? null : t));
    },
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDropTarget(null);
      const moved = e.dataTransfer.getData(MOVE_MIME);
      if (moved) return move(JSON.parse(moved) as string[], target);
      void filesFromDrop(e.dataTransfer).then((fs) => upload(fs, target));
    },
  });
  const dragRow = (f: Entry) => ({
    draggable: true,
    onDragStart: (e: React.DragEvent) => {
      const paths = selected.has(f.path) ? [...selected] : [f.path];
      e.dataTransfer.setData(MOVE_MIME, JSON.stringify(paths));
      e.dataTransfer.effectAllowed = "move";
    },
  });

  const toggle = (p: string) => setSelected((s) => { const n = new Set(s); if (n.has(p)) n.delete(p); else n.add(p); return n; });
  const allOn = items.length > 0 && items.every((f) => selected.has(f.path));
  const sel = [...selected];
  const crumbs = path ? path.split("/") : [];
  const r = rt.data?.runtime;
  const header = (key: Sort["key"], label: string, cls = "") => (
    <th className={cx("px-4 py-2 font-medium", cls)}>
      <button onClick={() => setSort((s) => ({ key, dir: s.key === key ? (s.dir === 1 ? -1 : 1) : 1 }))} className="hover:text-[var(--text)]">
        <span className="inline-flex items-center gap-1">{label}{sort.key === key && <Icon name={sort.dir === 1 ? "arrowUp" : "arrowDown"} size={12} />}</span>
      </button>
    </th>
  );

  return (
    <>
      <PageHeader
        title="Files"
        description="Your private workspace. Your assistant reads and writes files here; nobody else can see them. Drag files onto a folder to move them, or drop files and whole folders from your computer to upload."
        actions={
          <>
            <Button onClick={() => setNewFolder("")}>New folder</Button>
            {canUpload && (
              <>
                <Button onClick={() => folderInput.current?.click()}>Upload folder</Button>
                <Button variant="primary" onClick={() => fileInput.current?.click()}>Upload files</Button>
                <input ref={fileInput} type="file" multiple hidden onChange={(e) => { void upload(fromInput(e.target.files), path); e.target.value = ""; }} />
                {/* webkitdirectory isn't in React's types; every current browser supports it. */}
                <input ref={folderInput} type="file" multiple hidden {...{ webkitdirectory: "" }} onChange={(e) => { void upload(fromInput(e.target.files), path); e.target.value = ""; }} />
              </>
            )}
          </>
        }
      />

      <div className="mb-3 flex flex-wrap items-center gap-3">
        <nav className="flex min-w-0 flex-1 flex-wrap items-center gap-1 text-sm">
          <button {...dropProps("")} onClick={() => go("")} className={cx("rounded px-1.5 py-0.5 font-medium hover:bg-[var(--panel-2)]", dropTarget === "" && "bg-[var(--color-accent)]/15 ring-1 ring-[var(--color-accent)]")}>workspace</button>
          {crumbs.map((c, i) => {
            const p = crumbs.slice(0, i + 1).join("/");
            return (
              <span key={p} className="flex items-center gap-1">
                <span className="text-[var(--muted)]">/</span>
                <button {...dropProps(p)} onClick={() => go(p)} className={cx("rounded px-1.5 py-0.5 hover:bg-[var(--panel-2)]", dropTarget === p && "bg-[var(--color-accent)]/15 ring-1 ring-[var(--color-accent)]")}>{c}</button>
              </span>
            );
          })}
        </nav>
        <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter this folder" className="h-8 w-48 rounded-lg border border-[var(--line)] bg-[var(--panel)] px-2.5 text-sm outline-none focus:border-[var(--color-accent)]" />
        {r?.storageCapacityBytes ? <div className="w-44"><UsageBar used={r.storageUsedBytes} total={r.storageCapacityBytes} /></div> : null}
      </div>

      <ErrorText error={error ?? list.error} />

      {batch && (() => {
        const finished = batch.done + batch.failed;
        const running = finished < batch.total;
        const failed = uploads.filter((u) => u.error);
        const active = uploads.filter((u) => !u.error);
        return (
          <Card className="mb-3 divide-y divide-[var(--line)] text-sm">
            <div className="flex flex-wrap items-center gap-3 px-4 py-2.5">
              <span className="min-w-0 flex-1">
                {running ? `Uploading ${n(finished)} of ${n(batch.total)}…` : `Uploaded ${n(batch.done)} of ${n(batch.total)} file${batch.total === 1 ? "" : "s"}`}
                {batch.failed > 0 && <span className="text-red-700"> · {n(batch.failed)} failed</span>}
              </span>
              {running && (
                <span className="h-1.5 w-40 overflow-hidden rounded-full bg-[var(--panel-2)]">
                  <span className="block h-full bg-[var(--color-accent)] transition-all" style={{ width: `${Math.round((finished / batch.total) * 100)}%` }} />
                </span>
              )}
              {!running && failedFiles.current.size > 0 && <Button className="h-7 text-xs" onClick={retryFailed}>Retry failed</Button>}
              {!running && <button className="text-xs text-[var(--muted)] hover:text-[var(--text)]" onClick={dismissUploads}>Dismiss</button>}
            </div>
            {active.map((u) => (
              <div key={u.id} className="flex items-center gap-3 px-4 py-2">
                <span className="min-w-0 flex-1 truncate text-[var(--muted)]">{u.name}</span>
                <span className="h-1.5 w-40 overflow-hidden rounded-full bg-[var(--panel-2)]"><span className="block h-full bg-[var(--color-accent)] transition-all" style={{ width: `${Math.round(u.progress * 100)}%` }} /></span>
              </div>
            ))}
            {failed.slice(0, FAILED_SHOWN).map((u) => (
              <div key={u.id} className="flex items-center gap-3 px-4 py-2">
                <span className="min-w-0 flex-1 truncate">{u.name}</span>
                <span className="text-xs text-red-700">{u.error}</span>
              </div>
            ))}
            {failed.length > FAILED_SHOWN && <div className="px-4 py-2 text-xs text-[var(--muted)]">…and {n(failed.length - FAILED_SHOWN)} more failed</div>}
          </Card>
        );
      })()}

      {sel.length > 0 && (
        <div className="mb-2 flex flex-wrap items-center gap-2 rounded-lg bg-[var(--chrome)] px-3 py-2 text-sm text-white">
          <span className="mr-2">{sel.length} selected</span>
          <Button className="h-7 text-xs" onClick={() => setMoving(sel)}>Move to…</Button>
          <Button className="h-7 text-xs" onClick={() => sel.forEach((p) => { const f = items.find((x) => x.path === p); window.open(f?.type === "dir" ? `/api/me/assistant/files/zip?path=${encodeURIComponent(p)}` : fileUrl(p, true)); })}>Download</Button>
          <Button variant="danger" className="h-7 text-xs" onClick={() => confirm(`Delete ${sel.length} item${sel.length > 1 ? "s" : ""}? Folders are deleted with everything in them.`) && act.mutate(async () => { for (const p of sel) await unwrap(api.me.assistant.files.$delete({ query: { path: p } })); })}>Delete</Button>
          <button className="ml-auto text-xs text-white/70 hover:text-white" onClick={() => setSelected(new Set())}>Clear</button>
        </div>
      )}

      <Card className={cx("overflow-hidden transition", dropTarget === path && "ring-2 ring-[var(--color-accent)]/40")}>
        <div {...dropProps(path)} className="min-h-40">
          {newFolder !== null && (
            <form
              className="flex items-center gap-2 border-b border-[var(--line)] px-4 py-2"
              onSubmit={(e) => {
                e.preventDefault();
                const name = newFolder.trim();
                if (name) act.mutate(() => unwrap(api.me.assistant.files.mkdir.$post({ json: { path: join(path, name) } })));
                setNewFolder(null);
              }}
            >
              <Icon name="files" size={17} className="shrink-0 text-[var(--color-accent)]" />
              <input autoFocus value={newFolder} onChange={(e) => setNewFolder(e.target.value)} onBlur={() => setNewFolder(null)} placeholder="Folder name — press Enter" className="h-8 flex-1 rounded border border-[var(--line)] px-2 text-sm outline-none focus:border-[var(--color-accent)]" />
            </form>
          )}
          {list.isPending ? (
            <div className="grid place-items-center py-16"><Spinner /></div>
          ) : !items.length ? (
            <div className="px-6 py-16 text-center text-sm text-[var(--muted)]">
              {filter ? "Nothing matches." : canUpload ? <>This folder is empty. <b>Drop files or folders here</b>, or use Upload.</> : "This folder is empty."}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--line)] text-left text-xs text-[var(--muted)]">
                  <th className="w-10 px-4 py-2"><input type="checkbox" checked={allOn} onChange={() => setSelected(allOn ? new Set() : new Set(items.map((f) => f.path)))} /></th>
                  {header("name", "Name")}
                  {header("size", "Size", "hidden w-28 text-right sm:table-cell")}
                  {header("modified", "Modified", "hidden w-48 md:table-cell")}
                  {/* Row actions float over the row on hover, so they take no width; phones (no hover) use the selection bar. */}
                  <th className="hidden w-0 p-0 sm:table-cell" />
                </tr>
              </thead>
              <tbody>
                {items.map((f) => (
                  <tr
                    key={f.path}
                    {...dragRow(f)}
                    {...(f.type === "dir" ? dropProps(f.path) : {})}
                    className={cx(
                      "group border-b border-[var(--line)] last:border-0 hover:bg-[var(--panel-2)]",
                      selected.has(f.path) && "bg-[var(--panel-2)]",
                      dropTarget === f.path && "bg-[var(--color-accent)]/10 outline outline-2 -outline-offset-2 outline-[var(--color-accent)]",
                    )}
                  >
                    <td className="px-4 py-2"><input type="checkbox" checked={selected.has(f.path)} onChange={() => toggle(f.path)} /></td>
                    <td className="max-w-0 px-4 py-2">
                      <button onClick={() => (f.type === "dir" ? go(f.path) : setPreview(f.path))} className="flex w-full min-w-0 items-center gap-2.5 text-left">
                        <Icon name={icon(f)} size={17} className={cx("shrink-0", f.type === "dir" ? "text-[var(--color-accent)]" : "text-[var(--muted)]")} />
                        <span className="truncate">{f.name}</span>
                      </button>
                    </td>
                    <td className="hidden px-4 py-2 text-right text-[var(--muted)] sm:table-cell">{f.type === "file" ? fmtSize(f.size) : ""}</td>
                    <td className="hidden px-4 py-2 text-[var(--muted)] md:table-cell">{new Date(f.modified * 1000).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}</td>
                    <td className="relative hidden w-0 p-0 sm:table-cell">
                      <span className="invisible absolute inset-y-0 right-0 flex items-center gap-0.5 bg-[var(--panel-2)] pr-2 pl-4 group-focus-within:visible group-hover:visible">
                        <a href={f.type === "dir" ? `/api/me/assistant/files/zip?path=${encodeURIComponent(f.path)}` : fileUrl(f.path, true)}>
                          <Button variant="ghost" className="h-7 px-2 text-xs" title={f.type === "dir" ? "Download as .zip" : "Download"}>Download</Button>
                        </a>
                        <Button variant="ghost" className="h-7 px-2 text-xs" onClick={() => setMoving([f.path])}>Move</Button>
                        <Button variant="ghost" className="h-7 px-2 text-xs" onClick={() => { const name = prompt("Rename to", f.name)?.trim(); if (name && name !== f.name) act.mutate(() => unwrap(api.me.assistant.files.rename.$post({ json: { path: f.path, name } }))); }}>Rename</Button>
                        <Button variant="ghost" className="h-7 px-2 text-xs" onClick={() => confirm(`Delete ${f.name}${f.type === "dir" ? " and everything in it" : ""}?`) && act.mutate(() => unwrap(api.me.assistant.files.$delete({ query: { path: f.path } })))}>Delete</Button>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Card>
      <p className="mt-2 text-xs text-[var(--muted)]">Tip: select several items and drag them together. Folders download as .zip (up to 200 MB).</p>

      {moving && (
        <WorkspacePicker
          mode="folder"
          title={`Move ${moving.length} item${moving.length > 1 ? "s" : ""} to…`}
          confirmLabel="Move here"
          exclude={moving}
          onClose={() => setMoving(null)}
          onPick={([dest]) => {
            move(moving, dest!.path);
            setMoving(null);
          }}
        />
      )}
      <FilePreview path={preview} onClose={() => setPreview(null)} />
    </>
  );
}
